import React, {useCallback, useEffect, useRef, useState} from 'react';
import {useNavigate} from 'react-router';
import {Box, Avatar, Chip, Dialog, InputBase, Tooltip, Typography} from '@mui/material';
import {alpha, useTheme} from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CloseIcon from '@mui/icons-material/Close';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import HubOutlinedIcon from '@mui/icons-material/HubOutlined';
import {useSession} from '../../firebase/UserProvider';
import useSpotlightStore from './useSpotlightStore';
import useAiWidgetStore from '../../stores/useAiWidgetStore';
import useSourcePackageBuilderStore from '../../stores/useSourcePackageBuilderStore';
import {searchUnified, fetchSourcesList} from '../products/reportEditorPage/leftPanel/components/sourcesApi';
import {getReportsByUser} from '../../utils/api/reports';
import {subscribeToWhiteboards} from '../../firebase/firestore/whiteboards';
import {formatEntityMentionCounts, listKbEntities, searchKbEntities} from '../../utils/api/kbEntitySearch';
import {getEntityKBUrl, isLocalEntityRef} from '../../utils/entityLinks';
import {getEntityTypeChipColor, getEntityTypeLabel, isPlaceholderEntityType} from '../../utils/entityTypeMeta';
import {resolveEntityAvatarUrl} from '../../utils/entityAvatarUrl';
import UserAvatar from '../../components/UserAvatar';

/** Mirror of useOpenSearchHit.documentTypeToFileType — converts unified DocumentType to the file viewer's bucket. */
function documentTypeToFileType(documentType) {
    switch (documentType) {
        case 'PDF':   return 'pdf';
        case 'AUDIO': return 'audio';
        case 'VIDEO': return 'video';
        case 'IMAGE': return 'image';
        default:      return 'unknown';
    }
}

/**
 * Scope tabs — labels and sources params aligned with the report-editor search panel
 * (All / Files=data_items / Products=reports / Corpus / Highlights / Charts / Entities).
 */
const SCOPES = [
    {label: 'All',        sources: 'all',        canBrowse: false},
    {label: 'Files',      sources: 'data_items',  canBrowse: true},
    {label: 'Products',   sources: 'reports',     canBrowse: true},
    {label: 'Corpus',     sources: 'corpus',      canBrowse: false},
    {label: 'Highlights', sources: 'highlights',  canBrowse: false},
    {label: 'Charts',     sources: 'charts',      canBrowse: true},
    {label: 'Entities',   sources: 'entities',    canBrowse: true},
];
const CHARTS_SCOPE_IDX = 5;
const ENTITIES_SCOPE_IDX = 6;

const QUICK_NAV = [
    {label: 'Ops Insights',  abbr: 'O',  path: '/analytics'},
    {label: 'Workflow',      abbr: 'W',  path: '/workflow'},
    {label: 'Requirements',  abbr: 'R',  path: '/icr'},
    {label: 'Products',      abbr: 'P',  path: '/products'},
    {label: 'Charts',        abbr: 'Ch', path: '/charts'},
    {label: 'Experts',       abbr: 'E',  path: '/experts'},
    {label: 'Feeds',         abbr: 'F',  path: '/feed'},
    {label: 'KB',            abbr: 'K',  path: '/kb'},
    {label: 'Help',          abbr: 'H',  path: '/help'},
    {
        label: 'AI Assistant',
        abbr:  'A',
        hint:  'panel',
        action: () => {
            useAiWidgetStore.getState().setActiveTab('ai');
            useAiWidgetStore.getState().setOpen(true);
        },
    },
];

const DEBOUNCE_MS = 350;

/** Classify a unified search hit into a display label and type key. */
function getHitMeta(hit) {
    const kind = hit?.openTarget?.kind;
    if (kind === 'report_segment')      return {type: 'report',    label: 'Product'};
    if (kind === 'data_item_reference') return {type: 'data',      label: 'File'};
    if (kind === 'highlight')           return {type: 'highlight',  label: 'Highlight'};
    return                                     {type: 'corpus',    label: 'Corpus'};
}

/** Map SCOPES index → the documentStore value that identifies hits for that scope. */
const SCOPE_STORE = [null, 'data_item', 'report', 'corpus', 'highlight', 'chart', 'entity'];

/** Filter a full-scope result set down to what the active scope tab should show. */
function filterByScope(results, scopeIdx) {
    if (scopeIdx === CHARTS_SCOPE_IDX || scopeIdx === ENTITIES_SCOPE_IDX) return [];
    const store = SCOPE_STORE[scopeIdx];
    if (!store) return results; // All scope
    return results.filter((h) => h?.documentStore === store);
}

/** Count hits per scope → [all, files, products, corpus, highlights, charts, entities]. */
function countByScope(results, chartCount = 0, entityCount = 0) {
    const counts = [results.length + entityCount, 0, 0, 0, 0, chartCount, entityCount];
    for (const h of results) {
        const ds = h?.documentStore;
        if (ds === 'data_item') counts[1]++;
        else if (ds === 'report')    counts[2]++;
        else if (ds === 'highlight') counts[4]++;
        else                         counts[3]++;
    }
    return counts;
}

/** Client-side search of chart (whiteboard) records by title. */
function searchCharts(charts, q) {
    if (!q || !q.trim()) return charts;
    const qLow = q.trim().toLowerCase();
    return charts.filter((c) => {
        const title = (c.title || c.name || '').toLowerCase();
        return title.includes(qLow);
    });
}

/**
 * Extract a stable document-level key so we can deduplicate segment hits.
 * Uses openTarget.kind to avoid cross-store collisions (a corpus doc and a
 * report can share the same ID string but are different documents).
 */
function docKey(hit) {
    const t = hit?.openTarget || {};
    const kind = t.kind;
    if (kind === 'report_segment'      && t.reportId)  return `report:${t.reportId}`;
    if (kind === 'corpus_segment'      && t.docId)     return `corpus:${t.docId}`;
    if (kind === 'data_item_reference') {
        const fid = t.referenceId ?? t.canonicalFileId;
        if (fid) return `file:${fid}`;
    }
    if (kind === 'highlight'           && t.highlightId) return `highlight:${t.highlightId}`;
    return null; // unkeyed — always keep
}

/**
 * Deduplicate results to one hit per source document.
 *
 * For each document key we keep the segment that best represents the match:
 *  1. Prefer a segment whose snippet actually contains the query term (content match)
 *     over a segment that only matched via the document title.
 *  2. Among equally-preferred candidates, keep the first one (highest backend rank).
 *
 * After dedup, docs whose title contains the query are floated to the top so a
 * "Soloviev Report" appears before documents that merely mention soloviev in the body.
 */
function deduplicateByDoc(results, query) {
    if (!results || results.length === 0) return results || [];
    const q = (query || '').trim().toLowerCase();
    // Map from docKey → index in `best` array
    const keyToIdx = new Map();
    const best = [];

    for (const hit of results) {
        const key = docKey(hit);
        if (key === null) { best.push(hit); continue; } // unkeyed — always keep

        const snippetHasQ = q && (hit.snippet || '').toLowerCase().includes(q);

        if (!keyToIdx.has(key)) {
            keyToIdx.set(key, best.length);
            best.push(hit);
        } else {
            // A better segment for this doc: prefer content match over title-only match
            const prevIdx = keyToIdx.get(key);
            const prevSnippetHasQ = q && (best[prevIdx].snippet || '').toLowerCase().includes(q);
            if (snippetHasQ && !prevSnippetHasQ) {
                best[prevIdx] = hit; // upgrade to content-matched segment
            }
        }
    }

    // Float title-matched documents to the top
    if (q) {
        best.sort((a, b) => {
            const aTitle = (a.title || '').toLowerCase().includes(q) ? 1 : 0;
            const bTitle = (b.title || '').toLowerCase().includes(q) ? 1 : 0;
            return bTitle - aTitle;
        });
    }
    return best;
}

function spotlightQueryNorm(query) {
    return (query || '').trim().toLowerCase();
}

/**
 * Entity relevance on All, aligned with KB name rank (exact / prefix / contains)
 * plus a lower band for alias-only matches and untyped Wikidata candidates.
 */
function entityAllMatchScore(hit, query) {
    const q = spotlightQueryNorm(query);
    if (!q) return 0;
    const name = String(hit?.canonicalName || hit?.label || '').toLowerCase();
    if (name === q) return 1;
    if (name.startsWith(q)) return 0.92;
    if (name.includes(q)) return 0.85;
    if (queryMatchesCanonicalName(hit, query)) return 0.8;
    const isCandidate = hit?.draftCandidate === true;
    const isExternal = hit?.local === false;
    if (isExternal) return 0.38;
    if (isCandidate) return 0.45;
    return 0.52;
}

/**
 * Document relevance on All: prefer the unified [0,1] score, but lift title matches
 * so a named report/file can outrank a weak entity/candidate.
 */
function documentAllMatchScore(hit, query) {
    const q = spotlightQueryNorm(query);
    const backend = typeof hit?.score === 'number' && Number.isFinite(hit.score) ? hit.score : 0;
    if (!q) return backend;
    const title = String(hit?.title || '').toLowerCase();
    const snippet = String(hit?.snippet || '').toLowerCase();
    let derived = backend;
    if (title === q) derived = Math.max(derived, 0.97);
    else if (title.startsWith(q) || title.includes(q)) derived = Math.max(derived, 0.88);
    else if (snippet.includes(q)) derived = Math.max(derived, Math.max(backend, 0.55));
    else derived = Math.max(derived, 0.2);
    return derived;
}

/** Interleave KB/Wikidata hits with unified document hits by comparable match score. */
function mergeAllScopeHits(entityHits, docHits, query) {
    const scored = [];
    (entityHits || []).forEach((hit, order) => {
        scored.push({hit, score: entityAllMatchScore(hit, query), kind: 0, order});
    });
    (docHits || []).forEach((hit, order) => {
        scored.push({hit, score: documentAllMatchScore(hit, query), kind: 1, order});
    });
    scored.sort((a, b) => b.score - a.score || a.kind - b.kind || a.order - b.order);
    return scored.map((row) => row.hit);
}

/** Friendly relative date string for a hit's documentDate. */
function formatHitDate(dateVal) {
    if (!dateVal) return null;
    const d = new Date(dateVal);
    if (isNaN(d.getTime())) return null;
    const diffMs   = Date.now() - d.getTime();
    const diffDays = Math.floor(diffMs / 86400000);
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7)  return `${diffDays}d ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`;
    return d.toLocaleDateString('en-US', {month: 'short', day: 'numeric', year: 'numeric'});
}

/** Returns true when clicking the hit will navigate/open somewhere. */
function canNavigateHit(hit) {
    const kind = hit?.openTarget?.kind;
    if (!kind) return false;
    if (kind === 'report_segment') return Boolean(hit.openTarget.reportId);
    if (kind === 'highlight')      return Boolean(hit.openTarget.canonicalFileId);
    if (kind === 'data_item_reference') {
        return Boolean(hit.openTarget.referenceId ?? hit.openTarget.canonicalFileId);
    }
    if (kind === 'corpus_segment') return Boolean(hit.openTarget.docId);
    return false;
}

/**
 * Open a federated hit using the correct mechanism:
 *  - report_segment → react-router navigate to /products/:id/edit
 *  - corpus_segment / data_item_reference / highlight → SourcePackageBuilder overlay (Zustand)
 */
function openHit(hit, navigate) {
    const target = hit?.openTarget || {};
    const kind   = target.kind;

    if (kind === 'report_segment' && target.reportId) {
        navigate(`/products/${target.reportId}/edit`);
        return;
    }

    if (kind === 'corpus_segment' && target.docId != null) {
        useSourcePackageBuilderStore.getState().open({
            viewKind:     'corpus',
            documentId:   String(target.docId),
            segmentIndex: target.segmentIndex ?? null,
            selectedHit:  hit,
        });
        return;
    }

    if (kind === 'data_item_reference') {
        const fileId = target.referenceId != null
            ? String(target.referenceId)
            : (target.canonicalFileId != null ? String(target.canonicalFileId) : null);
        if (!fileId) return;
        useSourcePackageBuilderStore.getState().open({
            viewKind: 'file',
            filePayload: {
                fileId,
                canonicalFileId: target.canonicalFileId != null ? String(target.canonicalFileId) : null,
                fileName:        hit.title || null,
                fileType:        documentTypeToFileType(hit.documentType),
                sentenceHit:     hit.sentenceHit || null,
            },
            selectedHit: hit,
        });
        return;
    }

    if (kind === 'highlight') {
        const fileId = target.canonicalFileId != null ? String(target.canonicalFileId) : null;
        if (!fileId) return;
        useSourcePackageBuilderStore.getState().open({
            viewKind: 'file',
            filePayload: {
                fileId,
                canonicalFileId: fileId,
                fileName:  hit.title || null,
                fileType:  documentTypeToFileType(hit.documentType),
                fileSource: 'my',
                sentenceHit: hit.sentenceHit || {
                    segmentId:        target.segmentId ?? null,
                    highlightRegionId: target.highlightRegionId ?? null,
                    range: {
                        startOffset: target.startOffset ?? null,
                        endOffset:   target.endOffset ?? null,
                        blockIndex:  target.blockIndex ?? null,
                    },
                },
            },
            selectedHit: hit,
        });
    }
}

/** Extract a name string from a raw browse item (report or data-item). */
function extractBrowseName(item) {
    return item?.name || item?.title || item?.productName || 'Untitled';
}

/** Extract an ID string from a raw browse item. */
function extractBrowseId(item) {
    if (item?.entityRef) return String(item.entityRef);
    if (item?.id) return String(item.id);
    return null;
}

function isEntityHit(hit) {
    return Boolean(hit?.entityRef) && !hit?.openTarget;
}

/** True when every query token appears in the canonical name (vs alias/mention-only). */
function queryMatchesCanonicalName(hit, query) {
    const tokens = (query || '').trim().toLowerCase().split(/\s+/).filter(Boolean);
    if (tokens.length === 0) return true;
    const name = String(hit?.canonicalName || hit?.label || '').toLowerCase();
    return tokens.every((token) => name.includes(token));
}

function spotlightEntityAvatarUrl(hit) {
    const id = hit?.entityRef || hit?.id || '';
    const imageUrl = hit?.imageUrl || null;
    if (isLocalEntityRef(id)) {
        return imageUrl || `/api/entities/${encodeURIComponent(id)}/avatar`;
    }
    return resolveEntityAvatarUrl({
        wikidataId: id,
        imageUrl,
    });
}

function EntityResultAvatar({hit}) {
    const label = hit?.canonicalName || hit?.label || '?';
    const src = spotlightEntityAvatarUrl(hit);
    return (
        <Avatar
            src={src || undefined}
            alt=""
            data-testid="spotlight-entity-avatar"
            sx={{width: 28, height: 28, fontSize: 12, flexShrink: 0, bgcolor: 'action.selected'}}
        >
            {String(label).slice(0, 1).toUpperCase()}
        </Avatar>
    );
}

/** Normalize list API payloads that may be arrays or wrapped objects. */
function normalizeBrowsePayload(payload) {
    if (Array.isArray(payload)) return payload;
    const candidates = [payload?.items, payload?.content, payload?.data, payload?.results];
    return candidates.find(Array.isArray) ?? [];
}

/**
 * Trim snippet to at most maxLen chars, trying to centre the window on the
 * first occurrence of any query term so the match is always visible.
 */
function trimSnippet(text, query, maxLen = 150) {
    if (!text) return '';
    if (text.length <= maxLen) return text;

    const firstTerm = (query || '').trim().split(/\s+/).find(Boolean) || '';
    const matchIdx  = firstTerm
        ? text.toLowerCase().indexOf(firstTerm.toLowerCase())
        : -1;

    // If match is in the first window just take the front
    if (matchIdx === -1 || matchIdx < maxLen * 0.25) {
        return text.slice(0, maxLen) + '…';
    }
    // Centre the window so the match starts roughly 1/4 from the left
    const start = Math.max(0, matchIdx - Math.floor(maxLen * 0.25));
    const end   = Math.min(text.length, start + maxLen);
    return '…' + text.slice(start, end) + (end < text.length ? '…' : '');
}

/**
 * Inline text with each query term highlighted in yellow.
 * `sx` is forwarded to the wrapping Typography.
 */
function HighlightText({text, query, sx}) {
    if (!query || !text) {
        return <Typography component="span" sx={sx}>{text}</Typography>;
    }
    const terms   = [...new Set(query.trim().split(/\s+/).filter(Boolean))];
    const pattern = new RegExp(`(${terms.map((t) => t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})`, 'gi');
    const parts   = text.split(pattern);
    return (
        <Typography component="span" sx={sx}>
            {parts.map((part, i) =>
                i % 2 === 1  // split with capture group: odd indices are the captured matches
                    ? <mark key={i} style={{
                        background: 'rgba(250, 200, 40, 0.35)',
                        borderRadius: 2,
                        padding: '0 1px',
                        color: 'inherit',
                    }}>{part}</mark>
                    : part,
            )}
        </Typography>
    );
}

/* ── Keyboard hint badge ───────────────────────────────────────── */
function KbdBadge({children}) {
    const theme = useTheme();
    return (
        <Box
            component="span"
            sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                px: 0.75,
                py: 0.25,
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 0.75,
                fontSize: 11,
                fontFamily: 'monospace',
                color: 'text.secondary',
                minWidth: 24,
                userSelect: 'none',
            }}
        >
            {children}
        </Box>
    );
}

/* ── Hit type badge ────────────────────────────────────────────── */
function TypeBadge({label}) {
    const theme = useTheme();
    return (
        <Box
            component="span"
            sx={{
                fontSize: 10,
                fontWeight: 600,
                px: 0.75,
                py: 0.25,
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 0.5,
                color: 'text.secondary',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                flexShrink: 0,
                whiteSpace: 'nowrap',
            }}
        >
            {label}
        </Box>
    );
}

/** NER type chip — same outlined MUI Chip as /kb entity rows. */
function EntityTypeChip({entityType}) {
    const label = getEntityTypeLabel(entityType);
    if (!label || isPlaceholderEntityType(entityType)) return null;
    return (
        <Chip
            label={label}
            size="small"
            color={getEntityTypeChipColor(entityType)}
            variant="outlined"
            sx={{flexShrink: 0, height: 22, '& .MuiChip-label': {px: 0.75, fontSize: '0.68rem'}}}
        />
    );
}

/** Draft-report NER/NEL suggestion — same warning chip as /kb Candidates. */
function CandidateChip() {
    return (
        <Chip
            label="Candidate"
            size="small"
            color="warning"
            variant="outlined"
            sx={{flexShrink: 0, height: 22, '& .MuiChip-label': {px: 0.75, fontSize: '0.68rem'}}}
        />
    );
}

/** Corpus docs, data items, and products where the entity is mentioned. */
function EntityMentionCounts({entity}) {
    const label = formatEntityMentionCounts(entity);
    if (!label) return null;
    return (
        <Tooltip title="Mentions in corpus documents, data items, and products" arrow placement="top">
            <Typography
                component="span"
                variant="caption"
                data-testid="spotlight-entity-doc-count"
                sx={{color: 'text.disabled', flexShrink: 0, whiteSpace: 'nowrap', fontSize: 11}}
            >
                {label}
            </Typography>
        </Tooltip>
    );
}

/** Compact author avatar + name. Hidden when neither id nor name is present. */
function AuthorMeta({userId, displayName, hasAvatar}) {
    if (!userId && !displayName) return null;
    const name = displayName || userId;
    return (
        <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0}}>
            <UserAvatar
                user={{userId, displayName: name, hasAvatar: hasAvatar === true}}
                userId={userId}
                sx={{width: 16, height: 16, fontSize: 8, flexShrink: 0}}
            />
            <Typography
                variant="caption"
                sx={{color: 'text.secondary', fontSize: 11, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}
            >
                {name}
            </Typography>
        </Box>
    );
}

function authorFromSearchHit(hit) {
    const o = hit?.ownership;
    if (!o) return null;
    if (o.authorUserId || o.authorDisplayName) {
        return {
            userId: o.authorUserId,
            displayName: o.authorDisplayName,
            hasAvatar: o.authorHasAvatar === true,
        };
    }
    if (o.uploadedBy || o.uploadedByDisplayName) {
        return {
            userId: o.uploadedBy,
            displayName: o.uploadedByDisplayName,
            hasAvatar: true,
        };
    }
    return null;
}

function authorFromBrowseItem(item) {
    if (!item) return null;
    const userId = item.userId || item.ownerId || item.owner_id || item.uploadedBy || null;
    const displayName = item.userDisplayName || item.ownerDisplayName || item.uploadedByDisplayName || null;
    if (!userId && !displayName) return null;
    return {userId, displayName, hasAvatar: item.hasAvatar === true || Boolean(userId)};
}

/* ── Loading skeleton ──────────────────────────────────────────── */
function LoadingSkeleton() {
    const theme = useTheme();
    return (
        <Box sx={{px: 2, py: 1}}>
            {[75, 60, 80].map((w, n) => (
                <Box key={n} sx={{py: 1}}>
                    <Box sx={{height: 13, width: `${w}%`, bgcolor: alpha(theme.palette.action.hover, 0.25), borderRadius: 0.5, mb: 0.75}} />
                    <Box sx={{height: 10, width: '85%',   bgcolor: alpha(theme.palette.action.hover, 0.15), borderRadius: 0.5}} />
                </Box>
            ))}
        </Box>
    );
}

/* ── Section label ─────────────────────────────────────────────── */
function SectionLabel({children}) {
    return (
        <Typography
            variant="caption"
            sx={{
                display: 'block', px: 2, pt: 1.25, pb: 0.5,
                fontWeight: 700, letterSpacing: '0.06em',
                textTransform: 'uppercase', color: 'text.disabled',
            }}
        >
            {children}
        </Typography>
    );
}

/* ── Browse item row ───────────────────────────────────────────── */
function BrowseRow({name, Icon, selected, onClick, onMouseEnter, author, avatarHit, trailing}) {
    const theme = useTheme();
    return (
        <Box
            data-selected={selected ? 'true' : undefined}
            onMouseEnter={onMouseEnter}
            onClick={onClick}
            sx={{
                display: 'flex', alignItems: 'center', gap: 1.5,
                px: 2, py: 1,
                cursor: 'pointer',
                borderLeft: selected
                    ? `2px solid ${theme.palette.primary.main}`
                    : '2px solid transparent',
                bgcolor: selected
                    ? alpha(theme.palette.action.selected, 0.1)
                    : 'transparent',
            }}
        >
            {avatarHit
                ? <EntityResultAvatar hit={avatarHit} />
                : <Icon sx={{fontSize: 17, color: 'text.secondary', flexShrink: 0}} />}
            <Box sx={{flex: 1, minWidth: 0}}>
                <Box sx={{display: 'flex', alignItems: 'center', gap: 1, minHeight: 22}}>
                    <Typography
                        variant="body2"
                        sx={{fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1}}
                    >
                        {name}
                    </Typography>
                    {trailing}
                </Box>
                {author && (
                    <AuthorMeta userId={author.userId} displayName={author.displayName} hasAvatar={author.hasAvatar} />
                )}
            </Box>
        </Box>
    );
}

/* ── Scope hint (no list API available) ────────────────────────── */
function ScopeHint({label}) {
    return (
        <Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4, gap: 0.75}}>
            <SearchIcon sx={{fontSize: 32, color: 'text.disabled'}} />
            <Typography variant="body2" color="text.secondary">
                Type to search {label}
            </Typography>
        </Box>
    );
}

/* ── Main component ────────────────────────────────────────────── */

/**
 * Global CMD+K / Ctrl+K spotlight search overlay.
 *
 * Scopes match the report-editor search panel exactly:
 *   All | Files (data_items) | Products (reports) | Corpus | Highlights
 *
 * Empty-query behaviour per scope:
 *   All        → Quick navigation shortcuts
 *   Files      → User's data-item list (first 15)
 *   Products   → User's report list (first 15)
 *   Corpus     → "Type to search" hint (no list endpoint)
 *   Highlights → "Type to search" hint (no list endpoint)
 */
export default function SpotlightSearch() {
    const open             = useSpotlightStore((s) => s.open);
    const closeSpotlight   = useSpotlightStore((s) => s.closeSpotlight);
    const searchMode       = useSpotlightStore((s) => s.searchMode);
    const toggleSearchMode = useSpotlightStore((s) => s.toggleSearchMode);
    const {user}         = useSession();
    const navigate       = useNavigate();
    const theme          = useTheme();

    const [query,        setQuery]        = useState('');
    const [scopeIdx,     setScopeIdx]     = useState(0);
    const [results,      setResults]      = useState([]);
    const [loading,      setLoading]      = useState(false);
    const [searchError,  setSearchError]  = useState(null);
    const [selectedIdx,  setSelectedIdx]  = useState(0);
    const [browseItems,  setBrowseItems]  = useState([]);
    const [browseLoading,setBrowseLoading]= useState(false);
    const [allCharts,    setAllCharts]    = useState([]);
    const [entityResults, setEntityResults] = useState([]);

    const inputRef        = useRef(null);
    const bodyRef         = useRef(null);
    const abortRef        = useRef(null);
    const browseAbortRef  = useRef(null);
    const debounceRef     = useRef(null);
    // Set to true by handleScopeClick so the search fires immediately (no 350ms wait)
    const skipDebounceRef = useRef(false);

    /* Global Cmd+K / Ctrl+K — guards ProseMirror link shortcut */
    useEffect(() => {
        const handler = (e) => {
            if (!((e.metaKey || e.ctrlKey) && e.key === 'k')) return;
            if (document.activeElement?.closest?.('.ProseMirror')) return;
            e.preventDefault();
            useSpotlightStore.getState().toggleSpotlight();
        };
        window.addEventListener('keydown', handler, false);
        return () => window.removeEventListener('keydown', handler, false);
    }, []);

    /* Reset state and focus input each time the dialog opens */
    useEffect(() => {
        if (open) {
            setQuery('');
            setResults([]);
            setEntityResults([]);
            setBrowseItems([]);
            setSearchError(null);
            setSelectedIdx(0);
            setScopeIdx(0);
            setTimeout(() => inputRef.current?.focus(), 30);
        } else {
            if (abortRef.current)       { abortRef.current.abort();      abortRef.current = null;  }
            if (browseAbortRef.current) { browseAbortRef.current.abort(); browseAbortRef.current = null; }
            clearTimeout(debounceRef.current);
        }
    }, [open]);

    /* Load charts once when spotlight opens (used for Charts scope browse + search) */
    useEffect(() => {
        if (!open) return;
        const unsub = subscribeToWhiteboards(
            (rows) => setAllCharts(rows || []),
            () => setAllCharts([]),
        );
        return unsub;
    }, [open]);

    /* Browse effect — fires when query is empty and scope supports listing */
    useEffect(() => {
        if (!open) return;

        const q     = query.trim();
        const scope = SCOPES[scopeIdx];

        // Clear browse list when typing or on All/Corpus/Highlights
        if (q || !scope.canBrowse) {
            setBrowseItems([]);
            return;
        }

        if (browseAbortRef.current) { browseAbortRef.current.abort(); browseAbortRef.current = null; }
        const controller = new AbortController();
        browseAbortRef.current = controller;
        setBrowseLoading(true);

        const doFetch = async () => {
            try {
                let rawItems;
                if (scope.sources === 'charts') {
                    // Charts already loaded via subscribeToWhiteboards — no extra fetch needed
                    setBrowseItems(allCharts);
                    setSelectedIdx(0);
                    setBrowseLoading(false);
                    return;
                } else if (scope.sources === 'entities') {
                    rawItems = await listKbEntities(50);
                } else if (scope.sources === 'reports') {
                    const data = await getReportsByUser(user?.uid);
                    rawItems = normalizeBrowsePayload(data);
                } else {
                    const data = await fetchSourcesList({
                        view: {type: 'root'},
                        userId: user?.uid,
                        signal: controller.signal,
                    });
                    rawItems = normalizeBrowsePayload(data).filter(
                        (item) => item?.type !== 'folder' && item?.type !== 'FOLDER'
                            && item?.itemType !== 'folder' && item?.entityType !== 'folder',
                    );
                }
                if (!controller.signal.aborted) {
                    setBrowseItems(rawItems);
                    setSelectedIdx(0);
                }
            } catch (err) {
                if (!controller.signal.aborted && err?.name !== 'AbortError') {
                    setBrowseItems([]);
                }
            } finally {
                if (!controller.signal.aborted) setBrowseLoading(false);
            }
        };

        doFetch();
        return () => { controller.abort(); };
    }, [open, scopeIdx, query, user?.uid, scopeIdx === CHARTS_SCOPE_IDX ? allCharts : null]);

    /* Scroll the selected item into view when keyboard navigating */
    useEffect(() => {
        if (!bodyRef.current) return;
        const el = bodyRef.current.querySelector('[data-selected="true"]');
        el?.scrollIntoView({block: 'nearest'});
    }, [selectedIdx]);

    /* Debounced (or immediate) unified search — fires when query is non-empty */
    useEffect(() => {
        if (!open) return;

        clearTimeout(debounceRef.current);
        if (abortRef.current) { abortRef.current.abort(); abortRef.current = null; }

        const q = query.trim();
        if (!q) {
            setResults([]);
            setEntityResults([]);
            setLoading(false);
            setSearchError(null);
            return;
        }

        setLoading(true);
        setSearchError(null);

        // Scope changes (not typing) skip the debounce for instant feedback
        const delay = skipDebounceRef.current ? 0 : DEBOUNCE_MS;
        skipDebounceRef.current = false;

        debounceRef.current = setTimeout(() => {
            const controller = new AbortController();
            abortRef.current = controller;

            let pending = 2;
            const finish = () => {
                pending -= 1;
                if (pending <= 0 && !controller.signal.aborted) {
                    setLoading(false);
                }
            };

            searchKbEntities(q, 30)
                .then((payload) => {
                    if (controller.signal.aborted) return;
                    setEntityResults(payload?.results || []);
                })
                .catch(() => {
                    if (!controller.signal.aborted) setEntityResults([]);
                })
                .finally(finish);

            searchUnified({
                q,
                userId:        user?.uid || null,
                sources:       'all',
                mode:          searchMode,
                scope:         'all',
                uploadedBranch:'all',
                limit:         250,
                includeDrafts: true,
                signal:        controller.signal,
            })
                .then(({items}) => {
                    if (controller.signal.aborted) return;
                    setResults(items || []);
                    setSelectedIdx(0);
                })
                .catch((err) => {
                    if (controller.signal.aborted || err?.name === 'AbortError') return;
                    setSearchError('Search failed. Please try again.');
                    setResults([]);
                })
                .finally(finish);
        }, delay);

        return () => clearTimeout(debounceRef.current);
    }, [query, scopeIdx, user?.uid, open, searchMode]);

    const isEmpty          = query.trim().length === 0;
    const scope            = SCOPES[scopeIdx];
    const chartMatches     = isEmpty ? [] : searchCharts(allCharts, query);
    const docResults       = isEmpty ? results : deduplicateByDoc(filterByScope(results, scopeIdx), query);
    const visibleResults   = scopeIdx === CHARTS_SCOPE_IDX
        ? chartMatches
        : scopeIdx === ENTITIES_SCOPE_IDX
            ? (isEmpty ? [] : entityResults)
            : (isEmpty ? results : (scopeIdx === 0 ? mergeAllScopeHits(entityResults, docResults, query) : docResults));
    const dedupedAll       = deduplicateByDoc(results, query);
    const scopeCounts      = countByScope(dedupedAll, searchCharts(allCharts, query).length, entityResults.length);
    const showSearchSkeleton = !isEmpty && loading
        && visibleResults.length === 0
        && scopeIdx !== CHARTS_SCOPE_IDX;

    /* Determine what the "active list" is for keyboard navigation */
    const activeList = isEmpty
        ? (scopeIdx === 0 ? QUICK_NAV : browseItems)
        : visibleResults;

    /* Navigate to a selected item */
    const handleSelect = useCallback((idx) => {
        if (isEmpty) {
            if (scopeIdx === 0) {
                // Quick nav — items may use a route path OR a direct action
                const nav = QUICK_NAV[idx];
                if (nav) {
                    closeSpotlight();
                    if (nav.action) { nav.action(); } else { navigate(nav.path); }
                }
            } else {
                // Browse item (Files / Products / Charts / Entities)
                const item = browseItems[idx];
                if (!item) return;
                const id = extractBrowseId(item);
                closeSpotlight();
                if (scope.sources === 'entities' && id) {
                    navigate(getEntityKBUrl(id));
                } else if (scope.sources === 'charts' && id) {
                    navigate(`/charts/${id}`);
                } else if (scope.sources === 'reports' && id) {
                    navigate(`/products/${id}/edit`);
                } else if (scope.sources === 'data_items' && id) {
                    // Open data item file in the SourcePackageBuilder overlay
                    useSourcePackageBuilderStore.getState().open({
                        viewKind: 'file',
                        filePayload: {
                            fileId:         id,
                            canonicalFileId: id,
                            fileName:        extractBrowseName(item),
                            fileType:        'unknown',
                        },
                    });
                }
            }
            return;
        }

        const hit = visibleResults[idx];
        if (!hit) return;
        closeSpotlight();
        if (scopeIdx === CHARTS_SCOPE_IDX) {
            const id = hit.id;
            if (id) navigate(`/charts/${id}`);
            return;
        }
        if (scopeIdx === ENTITIES_SCOPE_IDX || isEntityHit(hit)) {
            const ref = hit.entityRef || hit.id;
            if (ref) navigate(getEntityKBUrl(ref));
            return;
        }
        openHit(hit, navigate);
    }, [isEmpty, scopeIdx, browseItems, visibleResults, scope, closeSpotlight, navigate]);

    /* Arrow / Enter keyboard navigation inside the open dialog */
    const handleKeyDown = useCallback((e) => {
        const len = activeList.length;
        if (e.key === 'Escape')    { closeSpotlight(); return; }
        if (e.key === 'ArrowDown') { e.preventDefault(); setSelectedIdx((i) => len ? (i + 1) % len : 0); return; }
        if (e.key === 'ArrowUp')   { e.preventDefault(); setSelectedIdx((i) => len ? (i - 1 + len) % len : 0); return; }
        if (e.key === 'Enter')     { e.preventDefault(); handleSelect(selectedIdx); }
    }, [activeList.length, closeSpotlight, selectedIdx, handleSelect]);

    const handleScopeClick = useCallback((i) => {
        skipDebounceRef.current = true;
        setScopeIdx(i);
        setSelectedIdx(0);
        setTimeout(() => inputRef.current?.focus(), 0);
    }, []);

    return (
        <Dialog
            open={open}
            onClose={closeSpotlight}
            maxWidth={false}
            TransitionProps={{timeout: 150}}
            slotProps={{
                backdrop: {
                    sx: {backgroundColor: alpha(theme.palette.common.black, 0.45)},
                },
                paper: {
                    elevation: 0,
                    sx: {
                        width: 806,
                        maxWidth: '96vw',
                        bgcolor: 'background.paper',
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: 2.5,
                        overflow: 'hidden',
                        mt: '10vh',
                        mb: 'auto',
                        mx: 'auto',
                    },
                },
            }}
        >
            {/* ── Search input ─────────────────────────────────────── */}
            <Box
                sx={{
                    display: 'flex', alignItems: 'center', gap: 1.5,
                    px: 2, py: 1.5,
                    borderBottom: `1px solid ${theme.palette.divider}`,
                }}
            >
                <SearchIcon sx={{fontSize: 20, color: 'text.disabled', flexShrink: 0}} />
                <InputBase
                    inputRef={inputRef}
                    fullWidth
                    value={query}
                    onChange={(e) => { setQuery(e.target.value); setSelectedIdx(0); }}
                    onKeyDown={handleKeyDown}
                    placeholder="Search everywhere…"
                    inputProps={{'aria-label': 'spotlight search'}}
                    sx={{fontSize: 16}}
                />
                {query
                    ? <CloseIcon
                        data-testid="spotlight-clear"
                        onClick={() => { setQuery(''); inputRef.current?.focus(); }}
                        sx={{fontSize: 18, color: 'text.disabled', cursor: 'pointer', flexShrink: 0}}
                      />
                    : null}
            </Box>

            {/* ── Scope tabs ───────────────────────────────────────── */}
            <Box
                sx={{
                    display: 'flex', gap: 0.5, px: 1.5, py: 1,
                    borderBottom: `1px solid ${theme.palette.divider}`,
                }}
            >
                {SCOPES.map((s, i) => {
                    const cnt = !isEmpty && scopeCounts[i] > 0 ? scopeCounts[i] : null;
                    return (
                        <Box
                            key={s.label}
                            component="button"
                            onClick={() => handleScopeClick(i)}
                            data-testid={`scope-tab-${s.label.toLowerCase()}`}
                            sx={{
                                border: 'none', cursor: 'pointer', borderRadius: 1,
                                px: 1.5, py: 0.5, fontSize: 13, fontFamily: 'inherit',
                                display: 'flex', alignItems: 'center', gap: 0.75,
                                bgcolor: i === scopeIdx ? 'primary.main' : 'transparent',
                                color:   i === scopeIdx ? 'primary.contrastText' : 'text.secondary',
                                fontWeight: i === scopeIdx ? 600 : 400,
                                transition: 'background-color 120ms, color 120ms',
                                '&:hover': {
                                    bgcolor: i === scopeIdx
                                        ? 'primary.dark'
                                        : theme.palette.action.hover,
                                    // Pin text so the hover background change never buries it
                                    color: i === scopeIdx ? 'primary.contrastText' : 'text.primary',
                                },
                            }}
                        >
                            {s.label}
                            {cnt !== null && (
                                <Box component="span" sx={{
                                    fontSize: 10, fontWeight: 700,
                                    lineHeight: 1, px: 0.6, py: 0.2,
                                    borderRadius: 0.75,
                                    bgcolor: i === scopeIdx
                                        ? alpha('#fff', 0.25)
                                        : alpha(theme.palette.action.selected, 0.15),
                                    color: i === scopeIdx ? 'inherit' : 'text.secondary',
                                    minWidth: 16, textAlign: 'center',
                                }}>
                                    {cnt}
                                </Box>
                            )}
                        </Box>
                    );
                })}
            </Box>

            {/* ── Body ─────────────────────────────────────────────── */}
            <Box ref={bodyRef} sx={{maxHeight: 516, overflowY: 'auto'}} data-testid="spotlight-body">

                {/* ── Empty query: All scope → quick nav ── */}
                {isEmpty && scopeIdx === 0 && (
                    <Box>
                        <SectionLabel>Quick navigation</SectionLabel>
                        {QUICK_NAV.map((item, i) => (
                            <Box
                                key={item.label}
                                data-testid={`quick-nav-${item.abbr.toLowerCase()}`}
                                data-selected={i === selectedIdx ? 'true' : undefined}
                                onMouseEnter={() => setSelectedIdx(i)}
                                onClick={() => handleSelect(i)}
                                sx={{
                                    display: 'flex', alignItems: 'center', gap: 1.5,
                                    px: 2, py: 1, cursor: 'pointer',
                                    bgcolor: i === selectedIdx
                                        ? alpha(theme.palette.action.selected, 0.12)
                                        : 'transparent',
                                    borderLeft: i === selectedIdx
                                        ? `2px solid ${theme.palette.primary.main}`
                                        : '2px solid transparent',
                                }}
                            >
                                <Box sx={{
                                    width: 28, height: 28, borderRadius: 1.5, flexShrink: 0,
                                    bgcolor: alpha(theme.palette.action.selected, 0.1),
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    fontSize: 12, fontWeight: 700, color: 'text.secondary',
                                }}>
                                    {item.abbr}
                                </Box>
                                <Typography variant="body2" sx={{fontWeight: 500, flex: 1}}>{item.label}</Typography>
                                <Typography variant="caption" sx={{color: 'text.disabled', fontFamily: 'monospace'}}>
                                    {item.hint ?? item.path}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                )}

                {/* ── Empty query: Files/Products scope → browse list ── */}
                {isEmpty && scope.canBrowse && (
                    browseLoading
                        ? <LoadingSkeleton />
                        : browseItems.length > 0
                            ? (
                                <Box>
                                    <SectionLabel>
                                        {scope.sources === 'reports' ? 'Your Products'
                                            : scope.sources === 'charts' ? 'Your Charts'
                                            : scope.sources === 'entities' ? 'Knowledge Base'
                                            : 'Your Files'}
                                    </SectionLabel>
                                    {browseItems.map((item, i) => (
                                        <BrowseRow
                                            key={extractBrowseId(item) ?? i}
                                            name={scope.sources === 'entities'
                                                ? (item.canonicalName || extractBrowseName(item))
                                                : extractBrowseName(item)}
                                            Icon={scope.sources === 'reports'
                                                ? ArticleOutlinedIcon
                                                : scope.sources === 'entities'
                                                    ? HubOutlinedIcon
                                                    : InsertDriveFileOutlinedIcon}
                                            author={scope.sources === 'entities' ? null : authorFromBrowseItem(item)}
                                            avatarHit={scope.sources === 'entities' ? item : null}
                                            trailing={scope.sources === 'entities'
                                                ? (
                                                    <>
                                                        <EntityMentionCounts entity={item} />
                                                        {item.draftCandidate === true
                                                            ? <CandidateChip />
                                                            : <EntityTypeChip entityType={item.entityType} />}
                                                    </>
                                                )
                                                : null}
                                            selected={i === selectedIdx}
                                            onMouseEnter={() => setSelectedIdx(i)}
                                            onClick={() => handleSelect(i)}
                                        />
                                    ))}
                                </Box>
                            )
                            : (
                                <Typography variant="body2" color="text.secondary"
                                    sx={{px: 2, py: 3, textAlign: 'center'}}>
                                    No {scope.label.toLowerCase()} found
                                </Typography>
                            )
                )}

                {/* ── Empty query: Corpus/Highlights → hint ── */}
                {isEmpty && !scope.canBrowse && scopeIdx !== 0 && (
                    <ScopeHint label={scope.label} />
                )}

                {/* ── Non-empty query: loading / error / results ── */}
                {!isEmpty && (
                    showSearchSkeleton
                        ? <LoadingSkeleton />
                        : searchError
                            ? (
                                <Typography variant="body2" color="error" sx={{px: 2, py: 2.5}}>
                                    {searchError}
                                </Typography>
                            )
                            : visibleResults.length === 0
                                ? (
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{px: 2, py: 2.5, textAlign: 'center'}}
                                    >
                                        No results for &ldquo;{query}&rdquo;
                                    </Typography>
                                )
                                : (
                                    <Box sx={{py: 0.5}} data-testid="spotlight-results">
                                        {visibleResults.map((hit, i) => {
                                            if (scopeIdx === CHARTS_SCOPE_IDX) {
                                                const isSelected = i === selectedIdx;
                                                const chartTitle = hit.title || hit.name || '(untitled)';
                                                const chartDate  = formatHitDate(hit.updatedAt || hit.updated_at || hit.createdAt || hit.created_at);
                                                const chartAuthor = authorFromBrowseItem(hit);
                                                return (
                                                    <Box
                                                        key={hit.id || i}
                                                        data-testid="spotlight-result-row"
                                                        data-selected={isSelected ? 'true' : undefined}
                                                        onMouseEnter={() => setSelectedIdx(i)}
                                                        onClick={() => handleSelect(i)}
                                                        sx={{
                                                            px: 2, py: 1, cursor: 'pointer',
                                                            borderLeft: isSelected
                                                                ? `2px solid ${theme.palette.primary.main}`
                                                                : '2px solid transparent',
                                                            bgcolor: isSelected ? alpha(theme.palette.action.selected, 0.1) : 'transparent',
                                                        }}
                                                    >
                                                        <Box sx={{display: 'flex', alignItems: 'center', gap: 1, mb: 0.25}}>
                                                            <HighlightText
                                                                text={chartTitle}
                                                                query={query}
                                                                sx={{fontWeight: 500, flex: 1, fontSize: 14, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block'}}
                                                            />
                                                            <TypeBadge label="Chart" />
                                                        </Box>
                                                        {(chartDate || chartAuthor) && (
                                                            <Box sx={{display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap'}}>
                                                                {chartAuthor && (
                                                                    <AuthorMeta
                                                                        userId={chartAuthor.userId}
                                                                        displayName={chartAuthor.displayName}
                                                                        hasAvatar={chartAuthor.hasAvatar}
                                                                    />
                                                                )}
                                                                {chartDate && (
                                                                    <Typography variant="caption" sx={{color: 'text.disabled', fontSize: 11}}>
                                                                        {chartDate}
                                                                    </Typography>
                                                                )}
                                                            </Box>
                                                        )}
                                                    </Box>
                                                );
                                            }
                                            if (scopeIdx === ENTITIES_SCOPE_IDX || isEntityHit(hit)) {
                                                const isSelected = i === selectedIdx;
                                                const entityTitle = hit.canonicalName || hit.label || hit.entityRef || '(untitled)';
                                                const aliasMention = !queryMatchesCanonicalName(hit, query);
                                                const snippet = hit.description || '';
                                                const showKindOnAll = scopeIdx === 0;
                                                const isDraftCandidate = hit.draftCandidate === true;
                                                return (
                                                    <Box
                                                        key={hit.entityRef || i}
                                                        data-testid="spotlight-result-row"
                                                        data-selected={isSelected ? 'true' : undefined}
                                                        onMouseEnter={() => setSelectedIdx(i)}
                                                        onClick={() => handleSelect(i)}
                                                        sx={{
                                                            px: 2, py: 1, cursor: 'pointer',
                                                            borderLeft: isSelected
                                                                ? `2px solid ${theme.palette.primary.main}`
                                                                : '2px solid transparent',
                                                            bgcolor: isSelected ? alpha(theme.palette.action.selected, 0.1) : 'transparent',
                                                        }}
                                                    >
                                                        <Box sx={{display: 'flex', gap: 1.25, alignItems: 'flex-start'}}>
                                                            <EntityResultAvatar hit={hit} />
                                                            <Box sx={{flex: 1, minWidth: 0}}>
                                                        <Box sx={{display: 'flex', alignItems: 'center', gap: 1, mb: 0.25}}>
                                                            <HighlightText
                                                                text={entityTitle}
                                                                query={query}
                                                                sx={{fontWeight: 500, flex: 1, fontSize: 14, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block'}}
                                                            />
                                                            {isDraftCandidate
                                                                ? <CandidateChip />
                                                                : (
                                                                    <>
                                                                        {showKindOnAll && !aliasMention
                                                                            ? <TypeBadge label="Entity" />
                                                                            : null}
                                                                        {(showKindOnAll || scopeIdx === ENTITIES_SCOPE_IDX) && aliasMention
                                                                            ? <TypeBadge label="Mention" />
                                                                            : null}
                                                                        <EntityTypeChip entityType={hit.entityType} />
                                                                    </>
                                                                )}
                                                            <EntityMentionCounts entity={hit} />
                                                        </Box>
                                                        {snippet ? (
                                                            <HighlightText
                                                                text={trimSnippet(snippet, query)}
                                                                query={query}
                                                                sx={{
                                                                    fontSize: 12, color: 'text.secondary',
                                                                    overflow: 'hidden', textOverflow: 'ellipsis',
                                                                    whiteSpace: 'nowrap', display: 'block',
                                                                }}
                                                            />
                                                        ) : null}
                                                            </Box>
                                                        </Box>
                                                    </Box>
                                                );
                                            }
                                            const {label}         = getHitMeta(hit);
                                            const isProduct       = hit?.documentStore === 'report';
                                            const inProductsScope = scopeIdx === 2;
                                            const title           = hit.title || '(untitled)';
                                            const rawSnippet      = hit.snippet || '';
                                            const snippet         = trimSnippet(rawSnippet, query);
                                            const canNavigate     = canNavigateHit(hit);
                                            const isSelected        = i === selectedIdx;
                                            const hitDate           = formatHitDate(hit.documentDate);
                                            // True when the query matched the document title but is absent
                                            // from the displayed snippet — tell the user why it appeared.
                                            const q = query.trim().toLowerCase();
                                            const titleHasQuery   = q && title.toLowerCase().includes(q);
                                            const snippetHasQuery = q && snippet.toLowerCase().includes(q);
                                            const titleMatchOnly  = titleHasQuery && !snippetHasQuery;
                                            // Sub-status: only for report hits
                                            const reportStatus      = isProduct && hit.reportStatus ? hit.reportStatus.toUpperCase() : null;
                                            const isDraft           = reportStatus && reportStatus !== 'APPROVED';
                                            const classification    = isProduct ? hit.ownership?.reportClassification : null;
                                            const secLevel          = isProduct ? hit.classificationLevel : null;
                                            const author            = authorFromSearchHit(hit);
                                            return (
                                                <Box
                                                    key={i}
                                                    data-testid="spotlight-result-row"
                                                    data-selected={isSelected ? 'true' : undefined}
                                                    onMouseEnter={() => setSelectedIdx(i)}
                                                    onClick={() => handleSelect(i)}
                                                    sx={{
                                                        px: 2, py: 1,
                                                        cursor: canNavigate ? 'pointer' : 'default',
                                                        borderLeft: isSelected
                                                            ? `2px solid ${theme.palette.primary.main}`
                                                            : '2px solid transparent',
                                                        bgcolor: isSelected
                                                            ? alpha(theme.palette.action.selected, 0.1)
                                                            : 'transparent',
                                                        opacity: canNavigate ? 1 : 0.6,
                                                    }}
                                                >
                                                    {/* Title row */}
                                                    <Box sx={{display: 'flex', alignItems: 'center', gap: 1, mb: 0.25}}>
                                                        <HighlightText
                                                            text={title}
                                                            query={query}
                                                            sx={{
                                                                fontWeight: 500, flex: 1, fontSize: 14,
                                                                overflow: 'hidden', textOverflow: 'ellipsis',
                                                                whiteSpace: 'nowrap', display: 'block',
                                                            }}
                                                        />
                                                        {/* Type badge only when not in a scoped tab — already obvious from the tab */}
                                                        {!inProductsScope && scopeIdx !== 1 && scopeIdx !== 3 && scopeIdx !== 4 && (
                                                            <TypeBadge label={label} />
                                                        )}
                                                    </Box>
                                                    {/* Snippet — or "matched in title" indicator */}
                                                    {titleMatchOnly
                                                        ? <Typography sx={{
                                                            fontSize: 11, color: 'primary.main',
                                                            fontStyle: 'italic', mb: 0.4, display: 'block',
                                                          }}>
                                                            Matched in title
                                                          </Typography>
                                                        : snippet
                                                            ? <HighlightText
                                                                text={snippet}
                                                                query={query}
                                                                sx={{
                                                                    fontSize: 12, color: 'text.secondary',
                                                                    lineHeight: 1.5, display: 'block', mb: 0.4,
                                                                }}
                                                              />
                                                            : null}
                                                    {/* Meta row: author + date + product sub-status badges */}
                                                    {(author || hitDate || reportStatus || classification || secLevel) && (
                                                        <Box sx={{display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.25, flexWrap: 'wrap'}}>
                                                            {author && (
                                                                <AuthorMeta
                                                                    userId={author.userId}
                                                                    displayName={author.displayName}
                                                                    hasAvatar={author.hasAvatar}
                                                                />
                                                            )}
                                                            {hitDate && (
                                                                <Typography variant="caption" sx={{color: 'text.disabled', fontSize: 11}}>
                                                                    {hitDate}
                                                                </Typography>
                                                            )}
                                                            {reportStatus && (
                                                                <Box component="span" sx={{
                                                                    fontSize: 10, fontWeight: 700, px: 0.75, py: 0.2,
                                                                    borderRadius: 0.5, border: '1px solid',
                                                                    borderColor: isDraft ? 'warning.main' : 'success.main',
                                                                    color: isDraft ? 'warning.dark' : 'success.dark',
                                                                    textTransform: 'uppercase', letterSpacing: '0.05em',
                                                                }}>
                                                                    {reportStatus}
                                                                </Box>
                                                            )}
                                                            {classification && (
                                                                <Box component="span" sx={{
                                                                    fontSize: 10, fontWeight: 600, px: 0.75, py: 0.2,
                                                                    borderRadius: 0.5, border: `1px solid ${theme.palette.divider}`,
                                                                    color: 'text.secondary',
                                                                    textTransform: 'uppercase', letterSpacing: '0.05em',
                                                                }}>
                                                                    {classification}
                                                                </Box>
                                                            )}
                                                            {secLevel && (
                                                                <Box component="span" sx={{
                                                                    fontSize: 10, fontWeight: 600, px: 0.75, py: 0.2,
                                                                    borderRadius: 0.5, border: `1px solid ${theme.palette.divider}`,
                                                                    color: 'text.secondary',
                                                                    letterSpacing: '0.05em',
                                                                }}>
                                                                    {secLevel}
                                                                </Box>
                                                            )}
                                                        </Box>
                                                    )}
                                                </Box>
                                            );
                                        })}
                                    </Box>
                                )
                )}
            </Box>

            {/* ── Footer keyboard hints ─────────────────────────────── */}
            <Box
                sx={{
                    display: 'flex', gap: 2, alignItems: 'center',
                    px: 2, py: 1,
                    borderTop: `1px solid ${theme.palette.divider}`,
                }}
            >
                {[['esc', 'close'], ['↑↓', 'navigate'], ['↵', 'open'], ['⌘K', 'toggle']].map(([key, label]) => (
                    <Box key={key} sx={{display: 'flex', gap: 0.75, alignItems: 'center'}}>
                        <KbdBadge>{key}</KbdBadge>
                        <Typography variant="caption" color="text.disabled">{label}</Typography>
                    </Box>
                ))}
                <Box sx={{flex: 1}} />
                {/* AI Search toggle */}
                <Tooltip title={searchMode === 'hybrid' ? 'AI Search on (hybrid) — click for keyword only' : 'Keyword search — click to enable AI Search'}>
                    <Box
                        onClick={toggleSearchMode}
                        sx={{
                            display: 'flex', gap: 0.5, alignItems: 'center',
                            cursor: 'pointer',
                            px: 1, py: 0.25,
                            borderRadius: 1,
                            border: `1px solid ${searchMode === 'hybrid'
                                ? theme.palette.primary.main
                                : theme.palette.divider}`,
                            color: searchMode === 'hybrid'
                                ? theme.palette.primary.main
                                : theme.palette.text.disabled,
                            '&:hover': {
                                bgcolor: alpha(theme.palette.primary.main, 0.06),
                            },
                            transition: 'all 0.15s',
                        }}
                    >
                        <AutoAwesomeIcon sx={{fontSize: 12}} />
                        <Typography variant="caption" sx={{fontWeight: searchMode === 'hybrid' ? 600 : 400}}>
                            AI
                        </Typography>
                    </Box>
                </Tooltip>
                {!isEmpty && visibleResults.length > 0 && (
                    <Typography variant="caption" color="text.disabled">
                        {visibleResults.length} result{visibleResults.length !== 1 ? 's' : ''}
                        {scopeIdx !== 0 && scopeIdx !== CHARTS_SCOPE_IDX && scopeIdx !== ENTITIES_SCOPE_IDX && scopeCounts[0] > visibleResults.length
                            ? ` · ${scopeCounts[0]} total`
                            : ''}
                    </Typography>
                )}
            </Box>
        </Dialog>
    );
}
